import React, { useEffect, useState, useCallback } from 'react';
import moment from 'moment';
import { TotalBookColor, PaperTradeKindColor } from '../../common/stylesConsts';
import { fetchConnector, FetchPNLAnalyticsPropsFromRedux } from '../dataFetcher';
import { optNumberFormat } from '../../containers/pivotHelper';
import { PNLAnalyticsEndpoint, PNLFetchStatus } from '../../actions/pnlAnalytics';
import { PNLGoodiesNonCompletedResponses } from '../PaperPlaceHolder';
import { PriceAnalyticsDataResp } from '../fetchTypes';
import LineGraph, { ChartType, LineGraphConfig } from '../../containers/LineGraph';
import { STANDARD_GRAPH_ELEMENT_HEIGHT } from '../../common/common';
import { postFetcher } from '../../common/fetcher';
import { getAuthUser } from '../../common/auth';
import { PNLMetaDataResp } from '../fetchTypes';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import InputLabel from '@material-ui/core/InputLabel';
import FormControl from '@material-ui/core/FormControl';

const TERM_MONTHS = {
    // JAN: 'F', Its already present since its default option
    FEB: 'G',
    MAR: 'H',
    APR: 'J',
    MAY: 'K',
    JUN: 'M',
    JUL: 'N',
    AUG: 'Q',
    SEP: 'U',
    OCT: 'V',
    NOV: 'X',
    DEC: 'Z',
};

const GROUPINGS = {
    // Q1: 'Q1', Its already present since its default option
    Q2: 'Q2',
    Q3: 'Q3',
    Q4: 'Q4',
    CAL: 'CAL',
};

const PRODUCTS = {
    // FEI: 'fei', Its already present since its default option
    FEI_C4: 'fei c4',
    CP: 'cp',
    CP_C4: 'cp c4',
    ARA: 'ara',
    ARA_C4: 'ara c4',
    EPC: 'epc',
    EPC_C4: 'epc c4',
    LST: 'lst',
    BRT: 'brt',
    WTI: 'wti',
    BLPG: 'blpg',
    BLPG3: 'blpg3',
    VLSFO: 'vlsfo',
    VLSFO_FUJ: 'vlsfo fuj',
    MOPJ: 'mopj',
    NWE_NAPHTHA: 'nwe naptha',
    ARB: 'arb',
    'E/W': 'e/w',
    'FEI/CP': 'fei/cp',
    'FEI/MOPJ': 'fei/mopj',
    'EPC/LST': 'epc/lst',
};

const BUCKETS = {
    // BALMO: 'balmo', Its already present since its default option
    MTH1: 'mth1',
    MTH2: 'mth2',
    MTH3: 'mth3',
    MTH4: 'mth4',
    MTH5: 'mth5',
    MTH6: 'mth6',
    MTH7: 'mth7',
    MTH8: 'mth8',
    MTH9: 'mth9',
    MTH10: 'mth10',
    MTH11: 'mth11',
    MTH12: 'mth12',
    MTH13: 'mth13',
    MTH14: 'mth14',
    MTH15: 'mth15',
    MTH16: 'mth16',
    MTH17: 'mth17',
    MTH18: 'mth18',
    MTH19: 'mth19',
    MTH20: 'mth20',
    MTH21: 'mth21',
};

const LOOKBACK_YEARS = {
    // '1': 1, Its already present since its default option
    '2': 2,
    '3': 3,
    '4': 4,
    '5': 5,
};

const getMenuItems = (obj: { [key: string]: string | number }) => {
    return Object.entries(obj).map(([key, value]) => (
        <MenuItem key={key} value={value}>
            {key}
        </MenuItem>
    ));
};

const lineGraphConfig: LineGraphConfig = {
    title: 'Historical Prices',
    dataKey: 'pipe_run_date',
    oyLabel: '',
    oxLabel: '',
    xAxisFormatter: (d) => moment(d).format('DD MMM'),
    xTooltipFormatter: (d) => moment(d).format('ddd MMM Do YYYY'),
    yAxis: {
        yLimit: ['dataMin', 'dataMax'],
        yAxisFormatter: (v) => optNumberFormat(v, 2, false, false, false),
    },
    yTooltipFormatter: (v) => optNumberFormat(v, 2, false, false, false),
    chartsSettings: [
        { chartType: ChartType.AreaAsLine, key: 'curve', color: TotalBookColor, title: 'Curve' },
        {
            chartType: ChartType.AreaAsLine,
            key: 'curve_lookback',
            color: PaperTradeKindColor,
            title: 'Curve Lookback',
            hiddenOnLoad: true,
        },
    ],
};

type Props = FetchPNLAnalyticsPropsFromRedux & {};

const HistPriceGraph = ({ pnlAnalytics, authUser, fetchLoadingAction, fetchCompletedAction, fetchFailedAction }: Props) => {
    const [mounted, setMounted] = useState(false);
    const pnl_list = (pnlAnalytics.meta.data! as PNLMetaDataResp).pnl_list;
    const [graphConfig, setGraphConfig] = useState({
        startDate: pnl_list[pnl_list.length - 1].pipe_run_date,
        endDate: pnl_list[0].pipe_run_date,
        product: 'fei',
        bucket: 'balmo',
        termMonth: 'F',
        grouping: 'Q1',
        lookbackYears: 1,
        priceBy: 'bucket',
    });
    const histPriceGraphData = pnlAnalytics.price_analytics;

    const fetchGraphData = useCallback(() => {
        postFetcher({
            url: '/pnl_analytics/price_analytics',
            data: graphConfig,
            headers: {
                'Swiss-Tech-Auth-Token': getAuthUser(authUser).swiss_token,
            },
            onStart: (cancelRequest) => {
                fetchLoadingAction({ endpoint: PNLAnalyticsEndpoint.PriceAnalytics, cancelRequest });
            },
            onSuccess: (res) => {
                fetchCompletedAction({ endpoint: PNLAnalyticsEndpoint.PriceAnalytics, data: res });
            },
            onFail: (err) => {
                fetchFailedAction({ endpoint: PNLAnalyticsEndpoint.PriceAnalytics, err });
            },
        });
    }, [graphConfig, authUser, fetchLoadingAction, fetchCompletedAction, fetchFailedAction]);

    // Fetch graph data on mount, once
    useEffect(() => {
        if (mounted) return;
        fetchGraphData();
        setMounted(true);
    }, [fetchGraphData, setMounted, mounted]);

    let respData = histPriceGraphData.data! as PriceAnalyticsDataResp;

    return (
        <div>
            <div style={{ display: 'flex', gap: '25px', marginBottom: '12px' }}>
                <TextField
                    id="start-date"
                    label="Start Date"
                    type="date"
                    value={graphConfig.startDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={(startDate) => {
                        setGraphConfig({ ...graphConfig, startDate: startDate.target.value });
                    }}
                />
                <TextField
                    id="end-date"
                    label="End Date"
                    type="date"
                    value={graphConfig.endDate}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={(endDate) => {
                        setGraphConfig({ ...graphConfig, endDate: endDate.target.value });
                    }}
                />
                <FormControl>
                    <InputLabel id="product-id">Product</InputLabel>
                    <Select
                        labelId="product-id"
                        id="product"
                        label="Product"
                        value={graphConfig.product}
                        defaultValue={'fei'}
                        onChange={(product) => {
                            setGraphConfig({ ...graphConfig, product: product.target.value as string });
                        }}
                        MenuProps={{
                            PaperProps: {
                                style: {
                                    maxHeight: 400,
                                },
                            },
                        }}>
                        <MenuItem value={'fei'}>FEI</MenuItem>
                        {getMenuItems(PRODUCTS)}
                    </Select>
                </FormControl>
                <FormControl>
                    <InputLabel id="price-by-id">Price By</InputLabel>
                    <Select
                        labelId="price-by-id"
                        id="price-by"
                        label="Price By"
                        value={graphConfig.priceBy}
                        defaultValue={'bucket'}
                        onChange={(priceBy) => {
                            setGraphConfig({ ...graphConfig, priceBy: priceBy.target.value as string });
                        }}>
                        <MenuItem value={'bucket'}>Bucket</MenuItem>
                        <MenuItem value={'termMonth'}>Term Month</MenuItem>
                        <MenuItem value={'grouping'}>Grouping</MenuItem>
                    </Select>
                </FormControl>
                {graphConfig.priceBy === 'bucket' && (
                    <FormControl>
                        <InputLabel id="bucket-id">Bucket</InputLabel>
                        <Select
                            labelId="bucket-id"
                            id="bucket"
                            label="Bucket"
                            value={graphConfig.bucket}
                            defaultValue={'balmo'}
                            onChange={(bucket) => {
                                setGraphConfig({ ...graphConfig, bucket: bucket.target.value as string });
                            }}
                            MenuProps={{
                                PaperProps: {
                                    style: {
                                        maxHeight: 400,
                                    },
                                },
                            }}>
                            <MenuItem value={'balmo'}>Balmo</MenuItem>
                            {getMenuItems(BUCKETS)}
                        </Select>
                    </FormControl>
                )}
                {graphConfig.priceBy === 'termMonth' && (
                    <FormControl>
                        <InputLabel id="term-month-id">Term</InputLabel>
                        <Select
                            labelId="term-month-id"
                            id="term-month"
                            label="Term Month"
                            value={graphConfig.termMonth}
                            defaultValue={'F'}
                            onChange={(termMonth) => {
                                setGraphConfig({ ...graphConfig, termMonth: termMonth.target.value as string });
                            }}>
                            <MenuItem value={'F'}>JAN</MenuItem>
                            {getMenuItems(TERM_MONTHS)}
                        </Select>
                    </FormControl>
                )}
                {graphConfig.priceBy === 'grouping' && (
                    <FormControl>
                        <InputLabel id="grouping-id">Grouping</InputLabel>
                        <Select
                            labelId="grouping-id"
                            id="grouping"
                            label="Grouping"
                            value={graphConfig.grouping}
                            defaultValue={'Q1'}
                            onChange={(grouping) => {
                                setGraphConfig({ ...graphConfig, grouping: grouping.target.value as string });
                            }}>
                            <MenuItem value={'Q1'}>Q1</MenuItem>
                            {getMenuItems(GROUPINGS)}
                        </Select>
                    </FormControl>
                )}
                <FormControl>
                    <InputLabel id="lookback-years-id">Lookback</InputLabel>
                    <Select
                        labelId="lookback-years-id"
                        id="lookback-years"
                        label="Lookback Years"
                        value={graphConfig.lookbackYears}
                        defaultValue={1}
                        onChange={(lookbackYears) => {
                            setGraphConfig({ ...graphConfig, lookbackYears: lookbackYears.target.value as number });
                        }}>
                        <MenuItem value={1}>1</MenuItem>
                        {getMenuItems(LOOKBACK_YEARS)}
                    </Select>
                </FormControl>
                <Button style={{ marginLeft: '25px' }} onClick={fetchGraphData} variant="contained" color="primary" size={'small'}>
                    Generate Graph
                </Button>
            </div>
            {histPriceGraphData.status !== PNLFetchStatus.COMPLETED ? (
                <PNLGoodiesNonCompletedResponses
                    endpoint={PNLAnalyticsEndpoint.PriceAnalytics}
                    data={histPriceGraphData}
                    minHeight={STANDARD_GRAPH_ELEMENT_HEIGHT}
                />
            ) : (
                <LineGraph {...lineGraphConfig} values={respData.price_analytics_graph} />
            )}
        </div>
    );
};

export default fetchConnector(HistPriceGraph);
